import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/reset.css';
import '../styles/nivaas-theme.css';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { AuthContext } from '../contexts/AuthContext';
const API_URL = process.env.REACT_APP_API_URL || 'https://nivaas.app';

// Use the manually uploaded wide logo from public directory

// Modern isolated wrapper for the landing page
const IsolatedPage = ({ children }) => {
  const cleanupRef = useRef(null);
  const observerRef = useRef(null);

  // Add Font Awesome if not already loaded
  useEffect(() => {
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    // Add Inter font if not already loaded
    if (!document.querySelector('link[href*="Inter"]')) {
      const linkPreconnect1 = document.createElement('link');
      linkPreconnect1.rel = 'preconnect';
      linkPreconnect1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(linkPreconnect1);

      const linkPreconnect2 = document.createElement('link');
      linkPreconnect2.rel = 'preconnect';
      linkPreconnect2.href = 'https://fonts.gstatic.com';
      linkPreconnect2.crossOrigin = true;
      document.head.appendChild(linkPreconnect2);

      const linkFont = document.createElement('link');
      linkFont.rel = 'stylesheet';
      linkFont.href = 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100..900;1,100..900&display=swap';
      document.head.appendChild(linkFont);
    }

    // Store original styles for cleanup
    const originalHtmlStyle = document.documentElement.getAttribute('style');
    const originalBodyStyle = document.body.getAttribute('style');
    const originalDataTheme = document.documentElement.getAttribute('data-bs-theme');

    cleanupRef.current = {
      originalHtmlStyle,
      originalBodyStyle,
      originalDataTheme,
      originalSetAttribute: document.documentElement.setAttribute,
      originalSetProperty: document.documentElement.style.setProperty
    };

    // Clean up any existing inline styles that might interfere
    const cleanupStyles = () => {
      // Remove all inline styles from HTML and body that might interfere
      document.documentElement.removeAttribute('style');
      document.body.removeAttribute('style');
      document.documentElement.removeAttribute('data-bs-theme');

      // Remove any existing theme style tags that might interfere
      const existingThemeStyles = document.querySelectorAll('#global-navigation-override, #theme-override-styles, #guard-dashboard-theme-styles, #sidebar-navigation-override, #sidebar-direct-styles, #app-custom-css-override');
      existingThemeStyles.forEach(style => {
        if (style && style.parentNode) {
          style.textContent = ''; // Clear content instead of removing to avoid conflicts
        }
      });

      // Force reset any CSS custom properties that might have been set
      const cssVarsToReset = [
        '--sidebar-bg-color', '--sidebar-text-color', '--sidebar-active-bg-color',
        '--bs-primary', '--bs-secondary', '--theme-primary-color', '--theme-primary',
        '--sidebar-width', '--sidebar-icon-color', '--sidebar-border-color',
        '--sidebar-hover-bg-color', '--sidebar-hover-text-color', '--block-title-color',
        '--block-title-bg-color', '--h1-color', '--h2-color', '--h3-color',
        '--text-primary-color', '--text-secondary-color', '--logo-width',
        '--theme-primary-rgb', '--bs-primary-rgb', '--bs-secondary-rgb',
        '--primary-color', '--secondary-color', '--accent-color',
        '--sidebar-active-text-color', '--sidebar-icon-color', '--block-title-bg-color'
      ];

      cssVarsToReset.forEach(cssVar => {
        document.documentElement.style.removeProperty(cssVar);
      });

      // Set a flag to prevent theme reapplication
      document.documentElement.setAttribute('data-landing-page-active', 'true');
    };

    // More aggressive cleanup with interval to catch any reapplication
    const aggressiveCleanup = () => {
      cleanupStyles();

      // Block any attempts to set inline styles on html
      document.documentElement.setAttribute = function (name, value) {
        if (name === 'style' && document.documentElement.getAttribute('data-landing-page-active') === 'true') {
          return;
        }
        return cleanupRef.current.originalSetAttribute.call(this, name, value);
      };

      // Block any attempts to set CSS properties directly
      document.documentElement.style.setProperty = function (property, value) {
        if (document.documentElement.getAttribute('data-landing-page-active') === 'true') {
          return;
        }
        return cleanupRef.current.originalSetProperty.call(this, property, value);
      };
    };

    // Apply initial cleanup
    aggressiveCleanup();

    // Set up continuous cleanup interval to catch any reapplication
    const cleanupInterval = setInterval(() => {
      if (document.documentElement.getAttribute('data-landing-page-active') === 'true') {
        const hasInlineStyle = document.documentElement.hasAttribute('style');
        if (hasInlineStyle) {
          cleanupStyles();
        }
      }
    }, 500);

    // Set up mutation observer to watch for style changes
    observerRef.current = new MutationObserver((mutations) => {
      let needsCleanup = false;

      mutations.forEach((mutation) => {
        // Check if style attribute was modified on html or body
        if ((mutation.target === document.documentElement || mutation.target === document.body) &&
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'style' || mutation.attributeName === 'data-bs-theme')) {
          needsCleanup = true;
        }

        // Check for new style elements being added
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'STYLE' &&
                (node.id === 'global-navigation-override' ||
                  node.id === 'theme-override-styles' ||
                  node.id === 'guard-dashboard-theme-styles' ||
                  node.id === 'sidebar-navigation-override' ||
                  node.id === 'sidebar-direct-styles' ||
                  node.id === 'app-custom-css-override')) {
                needsCleanup = true;
              }
            }
          });
        }
      });

      if (needsCleanup && document.documentElement.getAttribute('data-landing-page-active') === 'true') {
        cleanupStyles();
      }
    });

    // Start observing
    observerRef.current.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'data-bs-theme'],
      subtree: false
    });

    observerRef.current.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: false
    });

    observerRef.current.observe(document.head, {
      childList: true,
      subtree: false
    });

    // Cleanup function to restore original styles when component unmounts
    return () => {
      // Clear the cleanup interval
      clearInterval(cleanupInterval);

      // Remove landing page flag
      document.documentElement.removeAttribute('data-landing-page-active');

      // Restore original methods
      if (cleanupRef.current && cleanupRef.current.originalSetAttribute) {
        document.documentElement.setAttribute = cleanupRef.current.originalSetAttribute;
      }
      if (cleanupRef.current && cleanupRef.current.originalSetProperty) {
        document.documentElement.style.setProperty = cleanupRef.current.originalSetProperty;
      }

      // Disconnect observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (cleanupRef.current) {
        // Restore original styles
        if (cleanupRef.current.originalHtmlStyle) {
          document.documentElement.setAttribute('style', cleanupRef.current.originalHtmlStyle);
        } else {
          document.documentElement.removeAttribute('style');
        }

        if (cleanupRef.current.originalBodyStyle) {
          document.body.setAttribute('style', cleanupRef.current.originalBodyStyle);
        } else {
          document.body.removeAttribute('style');
        }

        if (cleanupRef.current.originalDataTheme) {
          document.documentElement.setAttribute('data-bs-theme', cleanupRef.current.originalDataTheme);
        } else {
          document.documentElement.removeAttribute('data-bs-theme');
        }
      }
    };
  }, []);

  return (
    <div className="modern-landing-wrapper">
      {children}
    </div>
  );
};

const SaasLanding = () => {
  const [plans, setPlans] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const sectionsRef = useRef({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handlePageContactClick = () => {
    navigate('/contact');
  };

  // Counter animation states
  const [countersVisible, setCountersVisible] = useState(false);
  const [counterValues, setCounterValues] = useState({
    societies: 0,
    residents: 0,
    uptime: 0
  });
  const countersRef = useRef(null);

  // Fetch subscription plans and testimonials from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch plans
        const plansResponse = await axios.get(`${API_URL}/api/saas/subscriptions/plans`);
        const processedPlans = plansResponse.data.map(plan => ({
          ...plan,
          billing_cycle: plan.billing_cycle || 'monthly', // Ensure billing_cycle is set
          features: Array.isArray(plan.features)
            ? plan.features
            : (typeof plan.features === 'string' ?
              (plan.features.startsWith('[') ? JSON.parse(plan.features) : [plan.features])
              : [])
        }));
        setPlans(processedPlans);

        // Fetch testimonials
        const testimonialsResponse = await axios.get(`${API_URL}/api/testimonials/public`);
        setTestimonials(testimonialsResponse.data || []);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Set up scroll-based active section detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      Object.entries(sectionsRef.current).forEach(([section, ref]) => {
        if (ref && ref.offsetTop <= scrollPosition &&
          ref.offsetTop + ref.offsetHeight > scrollPosition) {
          setActiveSection(section);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Counter animation effect
  useEffect(() => {
    const currentRef = countersRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !countersVisible) {
            setCountersVisible(true);
            // Add visible class to trigger entrance animation
            entry.target.classList.add('visible');
            animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [countersVisible]);

  const animateCounters = () => {
    const targets = {
      societies: 1000,
      residents: 50,
      uptime: 99.9
    };

    const animationDuration = 2500; // 2.5 seconds for smoother animation
    const steps = 100; // More steps for smoother counting
    const stepDuration = animationDuration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      // Use easing function for smoother animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const easedProgress = easeOutQuart;

      setCounterValues({
        societies: Math.floor(targets.societies * easedProgress),
        residents: Math.floor(targets.residents * easedProgress),
        uptime: parseFloat((targets.uptime * easedProgress).toFixed(1))
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounterValues(targets);
      }
    }, stepDuration);
  };

  const location = useLocation();

  const handleSectionClick = (sectionId) => {
    const section = sectionsRef.current[sectionId];
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      const section = sectionsRef.current[sectionId];

      if (section) {
        setTimeout(() => {
          window.scrollTo({
            top: section.offsetTop - 80,
            behavior: 'smooth',
          });
        }, 100); // Delay to ensure refs are set
      }
    }
  }, [location.hash]);

  const handleGetStarted = (planId) => {
    window.location.href = `/register?plan=${planId}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/landing');
  };

  useEffect(() => {
    // Add Inter font
    const linkInter = document.createElement('link');
    linkInter.rel = 'stylesheet';
    linkInter.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap';
    document.head.appendChild(linkInter);
    // Add Roboto font
    const linkRoboto = document.createElement('link');
    linkRoboto.rel = 'stylesheet';
    linkRoboto.href = 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100..900;1,100..900&display=swap';
    document.head.appendChild(linkRoboto);
  }, []);

  return (
    <IsolatedPage>
      <div className="modern-landing">
        {/* Navigation Header */}
        <Header
          activeSection={activeSection}
          onSectionClick={handleSectionClick}
          onLogout={handleLogout}
        />

        {/* Hero Section */}
        <section
          id="home"
          className="hero-section"
          ref={el => sectionsRef.current['home'] = el}
        >
          <div className="hero-background">
            <div className="hero-image-overlay"></div>
          </div>
          <div className="hero-container">
            <div className="hero-content">
              <div className="hero-badge">
                <span>🏡 Trusted by 1000+ residential societies</span>
              </div>
              <h1 className="hero-title">
                The Complete Society Management Platform
              </h1>
              <p className="hero-description">
                Streamline your residential community management with our comprehensive SaaS platform.
                From visitor management to financial tracking - everything you need in one place.
              </p>
              <div className="hero-actions">
                <Link to="/register" className="btn-primary large">
                  <span>Start Free Trial</span>
                  <i className="fa-solid fa-circle-arrow-right"></i>
                </Link>
                <Link to="/contact" className="btn-secondary large">
                  <i className="fa-solid fa-calendar-days"></i>
                  <span>Book Demo</span>
                </Link>
              </div>
              <div className="hero-features">
                <div className="feature-item">
                  <i className="fas fa-user-check"></i>
                  <span>Visitor Check-in</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-chart-line"></i>
                  <span>Financial Reports</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-mobile-alt"></i>
                  <span>Mobile Access</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Counters Section */}
        <section className="counters-section" ref={countersRef}>
          <div className="counters-container">
            <div className="counter-item">
              <div className="counter-content">
                <div className="counter-number">
                  <span className="number">{counterValues.societies}</span>
                  <span className="plus">+</span>
                </div>
                <div className="counter-label">Active <br />Societies</div>
              </div>
              <div className="counter-divider"></div>
            </div>

            <div className="counter-item">
              <div className="counter-content">
                <div className="counter-number">
                  <span className="number">{counterValues.residents}K</span>
                  <span className="plus">+</span>
                </div>
                <div className="counter-label">Happy <br />Residents</div>
              </div>
              <div className="counter-divider"></div>
            </div>

            <div className="counter-item">
              <div className="counter-content">
                <div className="counter-number">
                  <span className="number">{counterValues.uptime}</span>
                  <span className="percent">%</span>
                </div>
                <div className="counter-label">Uptime</div>
              </div>
            </div>
          </div>
        </section>

        {/* About Nivaas Section */}
        <section
          id="about"
          className="about-nivaas-section"
          ref={el => sectionsRef.current['about'] = el}
        >
          <div className="about-container">
            <div className="about-header">
              <span className="about-badge">About Nivaas</span>
              <h2 className="about-title">
                Everything You Need to Manage Your Society
              </h2>
              <p className="about-description">
                Comprehensive tools designed specifically for residential community management,
                making administration simple and residents happy.
              </p>

              <div className="about-stats">
                <div className="about-stat">
                  <span className="stat-number">15+</span>
                  <span className="stat-label">Core Features</span>
                </div>
                <div className="about-stat">
                  <span className="stat-number">99.9%</span>
                  <span className="stat-label">Uptime</span>
                </div>
                <div className="about-stat">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support</span>
                </div>
              </div>
            </div>

            <div className="about-images">
              <div className="about-image">
                <img
                  src="/images/nivaas-about-1.png"
                  alt="Society Management Feature 1"
                />
              </div>
              <div className="about-image">
                <img
                  src="/images/nivaas-about-2.png"
                  alt="Society Management Feature 2"
                />
              </div>
              <div className="about-image">
                <img
                  src="/images/nivaas-about-3.png"
                  alt="Society Management Feature 3"
                />
              </div>
              <div className="about-image">
                <img
                  src="/images/nivaas-about-4.png"
                  alt="Society Management Feature 4"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section
          id="features"
          className="features-section"
          ref={el => sectionsRef.current['features'] = el}
        >
          <div className="section-container">
            <div className="features-header">
              <span className="section-badge">Features</span>
              <h2 className="section-title">Our Powerful Features</h2>
              <p className="section-description">
                Make your society operations easier, smarter, and more connected with these key tools.
              </p>
            </div>

            <div className="features-content">
              <div className="features-grid">
                {/* Visitor Management */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <img src="/images/icons/visitor-management.svg" alt="Visitor Management" />
                  </div>
                  <h3 className="feature-title">Visitor Management</h3>
                  <p className="feature-description">
                    Streamline visitor entry with digital check-ins, pre-approvals, and real-time notifications to residents.
                  </p>
                  <ul className="feature-points">
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Digital visitor registration</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>QR code-based entry</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Resident notifications</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Visitor history tracking</span>
                    </li>
                  </ul>
                </div>

                {/* Financial Management */}
                <div className="feature-card featured">
                  <div className="feature-icon">
                    <img src="/images/icons/financial-management.svg" alt="Financial Management" />
                  </div>
                  <h3 className="feature-title">Financial Management</h3>
                  <p className="feature-description">
                    Complete financial transparency with automated billing, payment tracking, and detailed reporting.
                  </p>
                  <ul className="feature-points">
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Automated maintenance billing</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Online payment gateway</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Expense tracking</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Financial reports</span>
                    </li>
                  </ul>
                </div>

                {/* Complaint Management */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <img src="/images/icons/complaint-management.svg" alt="Complaint Management" />
                  </div>
                  <h3 className="feature-title">Complaint Management</h3>
                  <p className="feature-description">
                    Efficient complaint resolution system with priority tracking and automated status updates.
                  </p>
                  <ul className="feature-points">
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Priority-based ticketing</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Photo/document uploads</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Status tracking</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Resolution analytics</span>
                    </li>
                  </ul>
                </div>

                {/* Notice Board */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <img src="/images/icons/notice-board.svg" alt="Notice Board" />
                  </div>
                  <h3 className="feature-title">Notice Board</h3>
                  <p className="feature-description">
                    Keep everyone informed with digital notices, announcements, and important community updates.
                  </p>
                  <ul className="feature-points">
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Digital announcements</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Push notifications</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Read receipts</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Emergency alerts</span>
                    </li>
                  </ul>
                </div>

                {/* Mobile Access */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <img src="/images/icons/mobile-access.svg" alt="Mobile Access" />
                  </div>
                  <h3 className="feature-title">Mobile Access</h3>
                  <p className="feature-description">
                    Access all features on-the-go with our mobile-responsive platform and dedicated mobile app.
                  </p>
                  <ul className="feature-points">
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Responsive web design</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Mobile app available</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Offline capabilities</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Push notifications</span>
                    </li>
                  </ul>
                </div>

                {/* Security & Privacy */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <img src="/images/icons/security-privacy.svg" alt="Security & Privacy" />
                  </div>
                  <h3 className="feature-title">Security & Privacy</h3>
                  <p className="feature-description">
                    Enterprise-grade security with role-based access control and data encryption.
                  </p>
                  <ul className="feature-points">
                    <li>
                      <span className="check-icon">✓</span>
                      <span>End-to-end encryption</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Role-based permissions</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>Audit trails</span>
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      <span>GDPR compliant</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section
          id="testimonials"
          className="testimonials-section"
          ref={el => sectionsRef.current['testimonials'] = el}
        >
          <div className="section-container">
            <div className="testimonials-header">
              <span className="section-badge">Customer Stories</span>
              <h2 className="section-title">Trusted by Communities Nationwide</h2>
              <p className="section-description">
                Join thousands of satisfied societies who have transformed their management with Nivaas.
                See what administrators and residents are saying about our platform.
              </p>

              <div className="testimonials-stats">
                <div className="testimonials-stat">
                  <span className="stat-number">
                    {testimonials.length > 0
                      ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1)
                      : '4.9'
                    }/5
                  </span>
                  <span className="stat-label">Average Rating</span>
                </div>
                <div className="testimonials-stat">
                  <span className="stat-number">
                    1000+
                  </span>
                  <span className="stat-label">Happy Societies</span>
                </div>
                <div className="testimonials-stat">
                  <span className="stat-number">
                    {testimonials.length > 0
                      ? Math.round((testimonials.filter(t => t.rating >= 4).length / testimonials.length) * 100)
                      : 98
                    }%
                  </span>
                  <span className="stat-label">Satisfaction Rate</span>
                </div>
              </div>
            </div>

            <div className="testimonials-content">
              {testimonials.length > 0 ? (
                <Slider
                  dots={true}
                  infinite={true}
                  speed={500}
                  slidesToShow={3}
                  slidesToScroll={1}
                  responsive={[
                    {
                      breakpoint: 1024,
                      settings: {
                        slidesToShow: 2,
                        slidesToScroll: 1,
                      }
                    },
                    {
                      breakpoint: 768,
                      settings: {
                        slidesToShow: 1,
                        slidesToScroll: 1,
                      }
                    }
                  ]}
                  className="testimonials-slider"
                >
                  {testimonials.map((testimonial, index) => (
                    <div key={testimonial.id} className="testimonial-slide">
                      <div className="testimonial-card">
                        <div className="testimonial-header">
                          <div className="testimonial-rating-badge">
                            <span>{testimonial.rating}</span>
                          </div>
                          <div className="testimonial-stars">
                            {Array.from({ length: 5 }, (_, i) => (
                              <i key={i} className={`fas fa-star ${i < testimonial.rating ? 'filled' : ''}`}></i>
                            ))}
                          </div>
                        </div>
                        <div className="testimonial-quote-icon">
                          <i className="fas fa-quote-left"></i>
                        </div>
                        <p className="testimonial-text">
                          {testimonial.testimonial_text}
                        </p>
                        <div className="testimonial-author-row">
                          <div className="author-avatar-container">
                            {testimonial.profile_image ? (
                              <div className="author-avatar">
                                <img
                                  src={`${API_URL}/${testimonial.profile_image}`}
                                  alt={testimonial.author_name}
                                  style={{ width: '62px', height: '62px', objectFit: 'contain', borderRadius: '50%', background: 'linear-gradient(135deg, #3B185F 0%, #254A5D 100%);' }}
                                />
                              </div>
                            ) : (
                              <div className="author-avatar gradient-purple">
                                <span>{testimonial.author_name.charAt(0).toUpperCase()}</span>
                              </div>
                            )}
                            <div className="verified-badge">
                              <img src="/images/icons/shield-icon.png" alt="Verified" />
                            </div>
                          </div>
                          <div className="author-info">
                            <span className="author-name">{testimonial.author_name}</span>
                            <span className="author-title">{testimonial.author_title}</span>
                            <span className="author-company">{testimonial.author_company}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Slider>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted">No testimonials available at the moment.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section
          id="pricing"
          className="pricing-section"
          ref={el => sectionsRef.current['pricing'] = el}
        >
          <div className="pricing-background">
            <div className="pricing-overlay"></div>
          </div>
          <div className="section-container">
            <div className="pricing-header">
              <span className="section-badge">Pricing</span>
              <h2 className="section-title">Simple, Transparent Pricing</h2>
              <p className="section-description">
                Select a plan that fits your community and billing cycle.
              </p>

              <div className="pricing-toggle">
                <button
                  className={billingCycle === 'monthly' ? 'active' : ''}
                  onClick={() => setBillingCycle('monthly')}
                >
                  Monthly
                </button>
                <div
                  className="toggle-switch"
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annually' : 'monthly')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={`toggle-slider ${billingCycle === 'annually' ? 'active' : ''}`}></div>
                </div>
                <button
                  className={billingCycle === 'annually' ? 'active' : ''}
                  onClick={() => setBillingCycle('annually')}
                >
                  Annually
                </button>
              </div>
            </div>

            <div className="pricing-cards">
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading pricing plans...</p>
                </div>
              ) : (
                <div className="pricing-grid">
                  {(() => {
                    const filteredPlans = plans.filter(
                      plan => !plan.billing_cycle || plan.billing_cycle === billingCycle
                    );

                    if (filteredPlans.length === 0) {
                      return (
                        <div className="pricing-card no-plans-card">
                          <div className="pricing-header">
                            <p className="plan-description">No plans available right now.</p>
                          </div>
                        </div>
                      );
                    }

                    return filteredPlans.map(plan => (
                      <div key={plan.id} className={`pricing-card ${plan.name?.toLowerCase()}`}>
                        <div className="pricing-header">
                          <h3 className="plan-name">{plan.name}</h3>
                          <p className="plan-description">{plan.description}</p>
                        </div>
                        <div className="pricing-price">
                          <span className="price-currency">₹</span>
                          <span className="price-amount">{plan.price}</span>
                          <span className="price-period">
                            /user/{billingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>
                        <div className="pricing-features">
                          {plan.features.map((feature, index) => (
                            <div key={index} className="pricing-feature">
                              <div className="feature-check">
                                <i className="fas fa-check"></i>
                              </div>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => handleGetStarted(plan.id)}
                          className="btn-plan btn-outline"
                        >
                          Get Started <i className="fa-solid fa-circle-arrow-right"></i>
                        </button>
                      </div>
                    ));
                  })()}

                  {/* Custom Plan */}
                  <div className="pricing-card custom">
                    <div className="pricing-header">
                      <h3 className="plan-name">Custom</h3>
                      <p className="custom-description">Need something different?</p>
                      <p className="plan-description">Contact us for custom plans and pricing.</p>
                    </div>
                    <button
                      onClick={() => handlePageContactClick()}
                      className="btn-plan btn-outline"
                    >
                      Let Us Know <i className="fa-solid fa-circle-arrow-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="section-container">
            <div className="cta-content">
              <span className="section-badge">Contact Us</span>
              <h2 className="cta-title">Ready to Transform your Society Management?</h2>
              <p className="cta-description">
                Join thousands of satisfied communities using Nivaas. Start your free trial today!
              </p>
              <div className="cta-actions">
                <Link to="/register" className="btn-primary large">
                  Start Free Trial <i className="fa-solid fa-circle-arrow-right"></i>
                </Link>
                <Link to="/contact" className="btn-secondary large">
                  <i className="fa-solid fa-phone"></i> Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </IsolatedPage>
  );
}

export default SaasLanding;